import { createContext, useCallback, useContext, useMemo } from 'react'
import useSWR from 'swr'

const ActivityContext = createContext<{
  activities: Activity[]
  loading: boolean
  addActivity: (activity: Activity) => void
  removeActivity: (activityId: string) => void
  isPending: (activityId: string) => boolean
}>({
  activities: [],
  loading: false,
  addActivity: () => {},
  removeActivity: () => {},
  isPending: () => false,
})

type BaseActivity = {
  id: string
  type:
    | 'maintenance'
    | 'callback'
    | 'test'
    | 'repair'
    | 'oil-record'
    | 'pending-survey'
  unitName: string
  siteName: string
  title: string
  subtitle?: string
  date: string
}

type CallbackActivity = BaseActivity & {
  type: 'callback'
  entrapment: boolean
}

type MaintenanceActivity = BaseActivity & {
  type: 'maintenance'
  description: string
}

type TestActivity = BaseActivity & {
  type: 'test'
  description: string
}

type RepairActivity = BaseActivity & {
  type: 'repair'
  description: string
}

type OilRecordActivity = BaseActivity & {
  type: 'oil-record'
  description: string
  gallonsAdded: number
}

type PendingSurveyActivity = BaseActivity & {
  type: 'pending-survey'
}

export type Activity =
  | CallbackActivity
  | MaintenanceActivity
  | TestActivity
  | RepairActivity
  | OilRecordActivity
  | PendingSurveyActivity

export const ActivityProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { data: activities, isLoading: loading, mutate } = useSWR('/activities')

  // Create a Map for O(1) lookup performance
  const activityMap = useMemo(() => {
    const map = new Map<string, Activity>()
    if (activities) {
      for (const activity of activities) {
        map.set(activity.id, activity)
      }
    }
    return map
  }, [activities])

  const addActivity = useCallback(
    (activity: Activity): void => {
      mutate((activities: Activity[] | undefined) => {
        if (!activities) {
          return [activity]
        }
        return [...activities, activity]
      })
    },
    [mutate],
  )

  const removeActivity = useCallback(
    (activityId: string): void => {
      mutate((activities: Activity[] | undefined) => {
        if (!activities) {
          return []
        }
        return activities.filter((a) => a.id !== activityId)
      })
    },
    [mutate],
  )

  const isPending = useCallback(
    (activityId: string): boolean => {
      return activityMap.has(activityId)
    },
    [activityMap],
  )

  return (
    <ActivityContext.Provider
      value={{
        activities: activities ?? [],
        loading,
        addActivity,
        removeActivity,
        isPending,
      }}
    >
      {children}
    </ActivityContext.Provider>
  )
}

export const useActivity = () => {
  return useContext(ActivityContext)
}
